<template>
    <div class="flex h-full min-h-0 flex-col overflow-hidden bg-gray-50">
        <div class="flex-1 min-h-0 overflow-y-auto px-4 py-6 sm:px-6 lg:px-8">
            <div class="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <div>
                    <h1 class="text-2xl font-semibold text-gray-900">{{ $t('Notifications') }}</h1>
                    <p class="mt-1 text-sm text-gray-500">{{ $t('Manage all ticket notifications in one place') }}</p>
                </div>
                <div class="flex flex-wrap gap-2">
                    <button class="inline-flex items-center px-4 py-2 rounded-lg border border-gray-200 bg-white text-sm text-gray-700 hover:bg-gray-50" type="button" @click="fetchNotifications" :disabled="loading">
                        {{ $t('Refresh') }}
                    </button>
                    <button class="inline-flex items-center px-4 py-2 rounded-lg border border-blue-200 bg-blue-50 text-sm text-blue-700 hover:bg-blue-100 disabled:opacity-50" type="button" @click="markSelectedRead" :disabled="!hasSelection || processing">
                        {{ $t('Read selected') }}
                    </button>
                    <button class="inline-flex items-center px-4 py-2 rounded-lg border border-red-200 bg-red-50 text-sm text-red-700 hover:bg-red-100 disabled:opacity-50" type="button" @click="deleteSelected" :disabled="!hasSelection || processing">
                        {{ $t('Delete selected') }}
                    </button>
                </div>
            </div>

            <div class="mt-5 rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
                <div class="flex flex-col gap-3 border-b border-gray-100 px-4 py-4 sm:flex-row sm:items-center sm:justify-between">
                    <div class="flex items-center gap-3">
                        <img :src="notificationIcon" alt="Notification icon" class="h-10 w-10 rounded-xl border border-gray-200 bg-white p-2" />
                        <div>
                            <div class="text-sm font-semibold text-gray-900">{{ appHeaderTitle }}</div>
                            <div class="text-xs text-gray-500">{{ notifications.unread_total }} {{ $t('unread') }} / {{ notifications.items.length }} {{ $t('notifications') }}</div>
                        </div>
                    </div>
                    <label class="inline-flex items-center text-sm text-gray-600">
                        <input type="checkbox" class="rounded border-gray-300 text-blue-600 focus:ring-blue-500" :checked="allSelected" @change="toggleSelectAll($event.target.checked)">
                        <span class="ml-2">{{ $t('Select all') }}</span>
                    </label>
                </div>

                <div v-if="loading" class="px-6 py-10 text-sm text-gray-500">
                    {{ $t('Loading notifications') }}
                </div>
                <div v-else-if="notificationGroups.length < 1" class="px-6 py-10 text-sm text-gray-500">
                    {{ $t('No notifications yet') }}
                </div>
                <div v-else class="divide-y divide-gray-100">
                    <div
                        v-for="group in notificationGroups"
                        :key="group.key"
                        :class="group.unreadCount > 0 ? 'border-l-4 border-l-blue-500 bg-white' : 'border-l-4 border-l-transparent bg-white'"
                    >
                        <div class="px-4 py-4 sm:px-6">
                            <div class="flex items-start gap-3">
                                <input type="checkbox" class="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500" :checked="isGroupFullySelected(group)" @change="toggleGroupSelection(group, $event.target.checked)">
                                <div class="min-w-0 flex-1">
                                    <div class="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                                        <button class="min-w-0 flex-1 text-left focus:outline-none" type="button" @click="toggleGroup(group.key)">
                                            <div class="flex flex-wrap items-center gap-2">
                                                <h2 class="text-base font-semibold text-gray-900">{{ group.headerTitle }}</h2>
                                                <span v-if="group.unreadCount > 0" class="inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700">
                                                    {{ group.unreadCount }} {{ $t('unread') }}
                                                </span>
                                                <span class="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
                                                    {{ group.items.length }} {{ $t('messages') }}
                                                </span>
                                            </div>
                                            <div class="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-gray-500">
                                                <span>{{ group.appTitle }}</span>
                                                <span class="text-gray-300">&bull;</span>
                                                <span>{{ group.latestCreatedAt | momentFormatDateTimeAgo }}</span>
                                            </div>
                                            <p class="mt-2 break-words text-sm text-gray-600">{{ group.latestPreview }}</p>
                                        </button>
                                        <div class="flex flex-wrap gap-2 lg:justify-end">
                                            <button v-if="group.url" class="inline-flex items-center px-3 py-2 rounded-lg bg-blue-600 text-xs font-semibold text-white shadow-sm hover:bg-blue-700 disabled:opacity-50" type="button" :disabled="processing" @click="openGroup(group)">
                                                {{ $t('Open ticket') }}
                                            </button>
                                            <button class="inline-flex items-center px-3 py-2 rounded-lg border border-gray-200 bg-white text-xs text-gray-700 hover:bg-gray-50" type="button" @click="toggleGroup(group.key)">
                                                {{ isGroupExpanded(group.key) ? $t('Hide messages') : $t('Show messages') }}
                                            </button>
                                            <button v-if="group.unreadCount > 0" class="inline-flex items-center px-3 py-2 rounded-lg border border-blue-200 bg-blue-50 text-xs text-blue-700 hover:bg-blue-100 disabled:opacity-50" type="button" :disabled="processing" @click="markGroupRead(group)">
                                                {{ $t('Read this ticket') }}
                                            </button>
                                            <button class="inline-flex items-center px-3 py-2 rounded-lg border border-red-200 bg-red-50 text-xs text-red-700 hover:bg-red-100 disabled:opacity-50" type="button" :disabled="processing" @click="deleteGroup(group)">
                                                {{ $t('Delete all') }}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div v-if="isGroupExpanded(group.key)" class="border-t border-gray-100 bg-gray-50 px-4 py-4 sm:px-6">
                            <div class="max-h-[65vh] space-y-3 overflow-y-auto pr-1">
                                <div
                                    v-for="item in group.items"
                                    :key="item.id"
                                    class="rounded-xl border p-4 shadow-sm"
                                    :class="item.read_at ? 'border-gray-200 bg-white' : 'border-blue-200 bg-blue-50'"
                                >
                                    <div class="flex items-start gap-3">
                                        <input type="checkbox" class="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500" :checked="isSelected(item.id)" @change="toggleSelected(item.id, $event.target.checked)">
                                        <div class="min-w-0 flex-1">
                                            <div class="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                                                <div class="min-w-0 flex-1">
                                                    <div class="flex flex-wrap items-center gap-2">
                                                        <span v-if="item.sender_name" :class="item.read_at ? 'text-gray-700' : 'font-semibold text-gray-900'" class="text-sm truncate">
                                                            {{ item.sender_name }}
                                                        </span>
                                                        <span v-if="!item.read_at" class="inline-flex items-center rounded-full bg-blue-600 px-2 py-0.5 text-xs font-semibold text-white">
                                                            {{ $t('New') }}
                                                        </span>
                                                        <span class="text-xs text-gray-400">{{ item.created_at | momentFormatDateTimeAgo }}</span>
                                                    </div>
                                                    <p :class="item.read_at ? 'text-gray-700' : 'font-medium text-gray-900'" class="mt-1.5 text-sm break-words">{{ stripSenderPrefix(item.message_preview || item.message, item.sender_name) }}</p>
                                                </div>
                                                <div class="flex flex-wrap gap-2">
                                                    <button v-if="!item.read_at" class="inline-flex items-center px-3 py-2 rounded-lg border border-blue-200 bg-blue-50 text-xs text-blue-700 hover:bg-blue-100 disabled:opacity-50" type="button" :disabled="processing" @click="markSingleRead(item)">
                                                        {{ $t('Read') }}
                                                    </button>
                                                    <button v-if="canExpandItem(item)" class="inline-flex items-center px-3 py-2 rounded-lg border border-gray-200 bg-white text-xs text-gray-700 hover:bg-gray-50" type="button" @click="toggleItemExpanded(item.id)">
                                                        {{ isItemExpanded(item.id) ? $t('Hide full message') : $t('Expand message') }}
                                                    </button>
                                                    <button v-if="item.url" class="inline-flex items-center px-3 py-2 rounded-lg border border-gray-200 bg-white text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-50" type="button" :disabled="processing" @click="openNotification(item)">
                                                        {{ $t('Open ticket') }}
                                                    </button>
                                                    <button class="inline-flex items-center px-3 py-2 rounded-lg border border-red-200 bg-red-50 text-xs text-red-700 hover:bg-red-100 disabled:opacity-50" type="button" :disabled="processing" @click="deleteSingle(item)">
                                                        {{ $t('Delete') }}
                                                    </button>
                                                </div>
                                            </div>
                                            <div v-if="isItemExpanded(item.id)" class="mt-3 rounded-lg border border-gray-200 bg-gray-50 px-3 py-3 text-sm text-gray-700 whitespace-pre-line">
                                                {{ stripSenderPrefix(item.message_full || item.message_preview || item.message, item.sender_name) }}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'dashboard-notifications-page',
    data() {
        return {
            loading: false,
            processing: false,
            notifications: {
                items: [],
                unread_total: 0,
                unread_by_view: {
                    my: 0,
                    work: 0,
                    approve: 0,
                }
            },
            selectedIds: [],
            expandedGroupKey: null,
            expandedItemId: null,
        };
    },
    mounted() {
        this.fetchNotifications();
    },
    filters: {
        momentFormatDateTimeAgo(value) {
            return moment(value).locale(window.app.app_date_locale).fromNow();
        },
    },
    computed: {
        notificationIcon() {
            const base = window.app && window.app.url ? window.app.url : window.location.origin;
            return base.replace(/\/$/, '') + '/favicon.ico';
        },
        appHeaderTitle() {
            const appName = window.app && window.app.name ? window.app.name : 'Ticketing';
            const host = window.app && window.app.url ? new URL(window.app.url).host : window.location.host;
            return appName + ' - ' + host;
        },
        notificationGroups() {
            const groups = [];
            const map = {};
            (this.notifications.items || []).forEach((item) => {
                const key = item.ticket_uuid ? ('ticket:' + item.ticket_uuid) : ('notification:' + item.id);
                if (!map[key]) {
                    map[key] = {
                        key: key,
                        ticket_uuid: item.ticket_uuid || null,
                        url: null,
                        headerTitle: item.subject_line || item.title,
                        appTitle: item.title,
                        latestPreview: item.message_preview || item.message,
                        latestCreatedAt: item.created_at,
                        unreadCount: 0,
                        items: []
                    };
                    groups.push(map[key]);
                }
                map[key].items.push(item);
                if (!map[key].url && item.url) {
                    map[key].url = item.url;
                }
                if (!item.read_at) {
                    map[key].unreadCount += 1;
                }
            });
            return groups;
        },
        hasSelection() {
            return this.selectedIds.length > 0;
        },
        allSelected() {
            return this.notifications.items.length > 0 && this.selectedIds.length === this.notifications.items.length;
        },
    },
    methods: {
        fetchNotifications() {
            this.loading = true;
            axios.get('api/dashboard/notifications', {params: {limit: 200}}).then((response) => {
                this.notifications = response.data;
                this.selectedIds = this.selectedIds.filter((id) => this.notifications.items.some((item) => item.id === id));
                this.$store.commit('setNotificationUnreadTotal', response.data.unread_total || 0);
                window.dispatchEvent(new CustomEvent('dashboard-notifications-updated', {detail: response.data}));
            }).finally(() => {
                this.loading = false;
            });
        },
        isSelected(id) {
            return this.selectedIds.indexOf(id) > -1;
        },
        toggleSelected(id, checked) {
            if (checked) {
                if (!this.isSelected(id)) {
                    this.selectedIds.push(id);
                }
                return;
            }
            this.selectedIds = this.selectedIds.filter((itemId) => itemId !== id);
        },
        toggleSelectAll(checked) {
            this.selectedIds = checked ? this.notifications.items.map((item) => item.id) : [];
        },
        isGroupFullySelected(group) {
            return group.items.length > 0 && group.items.every((item) => this.isSelected(item.id));
        },
        toggleGroupSelection(group, checked) {
            const groupIds = group.items.map((item) => item.id);
            if (checked) {
                this.selectedIds = Array.from(new Set(this.selectedIds.concat(groupIds)));
                return;
            }
            this.selectedIds = this.selectedIds.filter((id) => groupIds.indexOf(id) === -1);
        },
        toggleGroup(key) {
            this.expandedGroupKey = this.expandedGroupKey === key ? null : key;
        },
        isGroupExpanded(key) {
            return this.expandedGroupKey === key;
        },
        toggleItemExpanded(id) {
            this.expandedItemId = this.expandedItemId === id ? null : id;
        },
        isItemExpanded(id) {
            return this.expandedItemId === id;
        },
        canExpandItem(item) {
            const full = this.stripSenderPrefix(item.message_full, item.sender_name).trim();
            const preview = this.stripSenderPrefix(item.message_preview || item.message, item.sender_name).trim();
            return full !== '' && full !== preview;
        },
        stripSenderPrefix(text, senderName) {
            const value = String(text || '');
            const sender = String(senderName || '').trim();
            if (!sender) {
                return value;
            }
            const escaped = sender.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            return value.replace(new RegExp('^' + escaped + '\\s*:\\s*'), '');
        },
        markSingleRead(item) {
            this.processing = true;
            axios.post('api/dashboard/notifications/' + item.id + '/mark-read').then(() => {
                this.fetchNotifications();
            }).finally(() => {
                this.processing = false;
            });
        },
        markGroupRead(group) {
            this.processing = true;
            const request = group.ticket_uuid
                ? axios.post('api/dashboard/notifications/tickets/' + group.ticket_uuid + '/mark-read')
                : axios.post('api/dashboard/notifications/mark-many-read', {ids: group.items.map((item) => item.id)});
            request.then(() => {
                this.fetchNotifications();
            }).finally(() => {
                this.processing = false;
            });
        },
        openGroup(group) {
            if (!group || !group.url || this.processing) {
                return;
            }

            const go = () => {
                this.$router.push(group.url);
            };

            if (group.unreadCount < 1) {
                go();
                return;
            }

            this.processing = true;
            const markRead = group.ticket_uuid
                ? axios.post('api/dashboard/notifications/tickets/' + group.ticket_uuid + '/mark-read')
                : axios.post('api/dashboard/notifications/mark-many-read', {ids: group.items.map((item) => item.id)});
            markRead.then(() => {
                this.fetchNotifications();
                go();
            }).finally(() => {
                this.processing = false;
            });
        },
        markSelectedRead() {
            if (!this.hasSelection) {
                return;
            }
            this.processing = true;
            axios.post('api/dashboard/notifications/mark-many-read', {ids: this.selectedIds}).then(() => {
                this.fetchNotifications();
            }).finally(() => {
                this.processing = false;
            });
        },
        deleteSingle(item) {
            this.processing = true;
            axios.delete('api/dashboard/notifications/' + item.id).then(() => {
                this.selectedIds = this.selectedIds.filter((id) => id !== item.id);
                this.removeItemsLocally([item.id]);
            }).finally(() => {
                this.processing = false;
            });
        },
        deleteSelected() {
            if (!this.hasSelection) {
                return;
            }
            this.processing = true;
            const ids = this.selectedIds.slice();
            axios.post('api/dashboard/notifications/delete-many', {ids: ids}).then(() => {
                this.selectedIds = [];
                this.removeItemsLocally(ids);
            }).finally(() => {
                this.processing = false;
            });
        },
        deleteGroup(group) {
            const ids = (group && group.items ? group.items : []).map((item) => item.id);
            if (ids.length < 1 || this.processing) {
                return;
            }
            if (!window.confirm(this.$i18n.t('Delete all notifications of this ticket?').toString())) {
                return;
            }
            this.processing = true;
            axios.post('api/dashboard/notifications/delete-many', {ids: ids}).then(() => {
                this.selectedIds = this.selectedIds.filter((id) => ids.indexOf(id) === -1);
                if (this.expandedGroupKey === group.key) {
                    this.expandedGroupKey = null;
                }
                this.removeItemsLocally(ids);
            }).finally(() => {
                this.processing = false;
            });
        },
        // Hapus item dari state lokal tanpa me-render ulang seluruh daftar,
        // supaya grup yang sedang di-expand dan posisi scroll tidak berubah.
        removeItemsLocally(ids) {
            const removed = this.notifications.items.filter((item) => ids.indexOf(item.id) > -1);
            if (removed.length < 1) {
                return;
            }

            this.notifications.items = this.notifications.items.filter((item) => ids.indexOf(item.id) === -1);

            const unreadRemoved = removed.filter((item) => !item.read_at);
            this.notifications.unread_total = Math.max(0, this.notifications.unread_total - unreadRemoved.length);
            unreadRemoved.forEach((item) => {
                if (item.view && this.notifications.unread_by_view[item.view] !== undefined) {
                    this.notifications.unread_by_view[item.view] = Math.max(0, this.notifications.unread_by_view[item.view] - 1);
                }
            });

            this.$store.commit('setNotificationUnreadTotal', this.notifications.unread_total);
            // Sinkronkan badge di navbar secara senyap (tanpa reload daftar ini).
            window.dispatchEvent(new CustomEvent('dashboard-refresh-notifications'));
        },
        openNotification(item) {
            const go = () => {
                if (item.url) {
                    this.$router.push(item.url);
                }
            };
            if (item.read_at) {
                go();
                return;
            }
            this.processing = true;
            axios.post('api/dashboard/notifications/' + item.id + '/mark-read').then(() => {
                this.fetchNotifications();
                go();
            }).finally(() => {
                this.processing = false;
            });
        },
    }
}
</script>
